import React, { useRef, useEffect } from "react";
import { Chart } from "chart.js/auto";
import { barChartOptions } from "../config/chartOptions";
import "chartjs-adapter-date-fns";

/**
 * Компонент иерархического графика для отображения аналитических данных
 *
 * @param {Object} props - Свойства компонента
 * @param {Object} props.data - Данные для графика
 * @param {string} props.title - Заголовок графика
 * @param {boolean} props.hideHeader - Скрыть заголовок (по умолчанию false)
 * @param {string} props.height - Высота графика в пикселях (по умолчанию 300px)
 */
const HierarchicalChart = ({
    data,
    title,
    hideHeader = false,
    height = "300px",
}) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (!data || !chartRef.current) return;

        // Очищаем предыдущий график, если он существует
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Создаем новый график
        const ctx = chartRef.current.getContext("2d");

        chartInstance.current = new Chart(ctx, {
            type: "bar",
            data,
            options: {
                ...barChartOptions,
                plugins: {
                    ...barChartOptions.plugins,
                    title: {
                        display: !hideHeader,
                        text: title || "Аналитика",
                        font: {
                            size: 16,
                            weight: "normal",
                        },
                        color: "#E5E7EB",
                    },
                    tooltip: {
                        ...barChartOptions.plugins.tooltip,
                        backgroundColor: "#1E1E1E",
                        titleColor: "#E5E7EB",
                        bodyColor: "#E5E7EB",
                        borderColor: "#3E3E42",
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || "";
                                const value = context.parsed.y || 0;
                                return `${label}: ${value} случаев`;
                            },
                        },
                    },
                },
                scales: {
                    ...barChartOptions.scales,
                    x: {
                        ...barChartOptions.scales.x,
                    },
                    y: {
                        ...barChartOptions.scales.y,
                    },
                },
            },
        });

        // Очистка при размонтировании
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [data, title, hideHeader]);

    if (!data) {
        return (
            <div
                className="flex justify-center items-center bg-[#2D2D30] rounded border border-[#3E3E42]"
                style={{ height }}
                role="alert"
                aria-label="Нет данных для отображения"
            >
                <p className="text-gray-400">Нет данных для отображения</p>
            </div>
        );
    }

    return (
        <div
            style={{ height }}
            role="img"
            aria-label={title || "Аналитический график"}
        >
            <canvas ref={chartRef} />
        </div>
    );
};

export default HierarchicalChart;
